import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { ApiUpsertSettingsBanner, ApiUpsertSettingsFavicon, ApiUpsertSettingsLogo } from "@/legacy/ApiCallerOld";
import { Button } from "../../Components/Button/Button";
import ApiDropdown from "../../Components/FormComponents/ApiDropdown/ApiDropdown";
import ImageUpload from "../../Components/FormComponents/ImageUpload/ImageUpload";
import OtherFunctions from "../../Helpers/OtherFunctions";
import { TicketClosePolicy, TicketRetentionPolicy } from "@shared/Enums/Enums";
import { FormParent } from "@shared/Components/Form/FormParent";
import { FormSection } from "@shared/Components/Form/FormSection";
import TextInput from "@shared/Components/FormComponents/TextInput/TextInput";
import { Entities } from "@shared/Entities/Entities";
import Dropdown from "@shared/Components/FormComponents/Dropdown/Dropdown";
import { Setting } from "@shared/Entities/EntityTypes";

interface SettingsHelpDeskProps {
	data?: Setting[];
	updateData(settingsData: Setting[]): void;
	refreshData?(): void;
}

export function SettingsHelpDesk(props: SettingsHelpDeskProps) {
	const [data, setData] = useState<Map<string, string | undefined | null> | undefined>();
	const { handleSubmit, getValues } = useForm();
	const [logo, setLogo] = useState<any>({});
	const [favicon, setFavicon] = useState<any>({});
	const [banner, setBanner] = useState<any>({});
	const navigate = useNavigate();

	useEffect(() => {
		const settingsMap = new Map();

		if (props.data != null) {
			props.data.forEach((setting) => {
				settingsMap.set(setting.key, setting.value);
			});
		}

		setData(settingsMap);
	}, [props.data]);



	async function formSubmit() {
		if (data != null) {
			const dataCopy = new Map(data);

			// Logo
			if (Object.keys(logo).length !== 0) {
				const logoPath = data.get("helpdesk.logo");

				const response = await ApiUpsertSettingsLogo(logoPath ?? "", logo.pictureData, logo.pictureMimetype);

				if (response.successful) {
					const newFileName = String(response.data);

					// Push the logo without waiting for async state change
					dataCopy.set("helpdesk.logo", newFileName);
				} else {
					toast.error("Error. " + response.errorCode + ": " + response.errorMsg);
				}
			}

			// Favicon
			if (Object.keys(favicon).length !== 0) {
				const faviconPath = data.get("helpdesk.favicon.path");

				const response = await ApiUpsertSettingsFavicon(faviconPath ?? "", favicon.pictureData, favicon.pictureMimetype);

				if (response.successful) {
					const newFileName = response.data == undefined ? null : response.data;

					// Push the favicon without waiting for async state change
					dataCopy.set("helpdesk.favicon.path", newFileName);

					toast.success("Favicon updated. Please refresh the page.");
				} else {
					toast.error("Error. " + response.errorCode + ": " + response.errorMsg);
				}
			}

			// Banner
			if (Object.keys(banner).length !== 0) {
				const bannerPath = data.get("helpdesk.banner.path");

				const response = await ApiUpsertSettingsBanner(bannerPath ?? "", banner.pictureData, banner.pictureMimetype);

				if (response.successful) {
					const newFileName = response.data == undefined ? null : response.data;

					// Push the banner without waiting for async state change
					dataCopy.set("helpdesk.banner.path", newFileName);

					toast.success("Banner updated. Please refresh the page.");
				} else {
					toast.error("Error. " + response.errorCode + ": " + response.errorMsg);
				}
			}

			const settings: Setting[] = [];

			dataCopy.forEach((value, key) => {
				settings.push({ key: key, value: value });
			});


			props.updateData(settings);
			navigate("/config/");
		}
	}

	function handleChangeNumber(key: string, newValue: number | undefined) {
		if (newValue != null) {
			handleChange(key, String(newValue));
		} else {
			handleChange(key, null);
		}
	}

	function handleChange(key: string, newValue: string | null | undefined) {
		const dataCopy = new Map(data ?? []);

		if (newValue != null) {
			dataCopy.set(key, newValue);
		} else {
			dataCopy.set(key, undefined);
		}

		setData(dataCopy);
	}

	function newLogoCallback(dataBase64: string, mimeType: string) {
		let newData = Object.assign({}, getValues());
		newData = OtherFunctions.SafeChangeProperty(newData, "pictureData", dataBase64);
		newData = OtherFunctions.SafeChangeProperty(newData, "pictureMimetype", mimeType);

		setLogo(newData);
	}

	function newFaviconCallback(dataBase64: string, mimeType: string) {
		let newData = Object.assign({}, getValues());
		newData = OtherFunctions.SafeChangeProperty(newData, "pictureData", dataBase64);
		newData = OtherFunctions.SafeChangeProperty(newData, "pictureMimetype", mimeType);

		setFavicon(newData);
	}

	function newKbBannerCallback(dataBase64: string, mimeType: string) {
		let newData = Object.assign({}, getValues());
		newData = OtherFunctions.SafeChangeProperty(newData, "pictureData", dataBase64);
		newData = OtherFunctions.SafeChangeProperty(newData, "pictureMimetype", mimeType);

		setBanner(newData);
	}

	function cancelButtonClick() {
		navigate("/config");
	}

	const defaultEmailChannel = !isNaN(parseInt(data?.get("helpdesk.defaultemailchannel") ?? "")) ? parseInt(data?.get("helpdesk.defaultemailchannel") ?? "") : null;

	const closeOptions = [
		{ label: "Never", value: TicketClosePolicy.NEVER },
		{ label: "After 1 Week", value: TicketClosePolicy.ONE_WEEK },
		{ label: "After 1 Month", value: TicketClosePolicy.ONE_MONTH },
		{ label: "After 3 Months", value: TicketClosePolicy.THREE_MONTHS }
	];

	const retentionOptions = [
		{ label: "Never", value: TicketRetentionPolicy.NEVER },
		{ label: "After 1 Month", value: TicketRetentionPolicy.ONE_MONTH },
		{ label: "After 3 Months", value: TicketRetentionPolicy.THREE_MONTHS },
		{ label: "After 1 Year", value: TicketRetentionPolicy.ONE_YEAR }
	];

	return (
		props.data != null ?
			<form onSubmit={handleSubmit(formSubmit)} >
				<FormParent
					title="Help Desk"
					description="General settings to control your help desk."
				>
					<FormSection>
						<TextInput
							value={data?.get("helpdesk.name") ?? undefined}
							dataname="helpdesk.name"
							handleSubmit={handleChange}
							label="Help desk name"
						/>

						<ApiDropdown
							value={defaultEmailChannel}
							entity={Entities.CHANNELSEMAIL}
							searchableField="name"
							dataname="helpdesk.defaultemailchannel"
							onChange={handleChangeNumber}
							label="Default email channel"
							mandatory
						/>
					</FormSection>

					<FormSection title="Data Retention">
						<Dropdown
							value={data?.get("tickets.closePolicy") ?? TicketClosePolicy.NEVER}
							mandatory
							dataname="tickets.closePolicy"
							options={closeOptions}
							onChange={handleChange}
							label="Delete closed tickets"
							toolTip="Closed tickets will be automatically deleted after this amount of time."
						/>

						<Dropdown
							value={data?.get("tickets.retentionPolicy") ?? TicketRetentionPolicy.NEVER}
							mandatory
							dataname="tickets.retentionPolicy"
							options={retentionOptions}
							onChange={handleChange}
							label="Permanently remove deleted tickets"
							toolTip="Tickets will be automatically removed after this amount of time."
						/>
					</FormSection>


					<FormSection title="White labeling">
						<div className="my-5">
							<TextInput
								value={data?.get("helpdesk.defaultdomain") ?? undefined}
								dataname="helpdesk.defaultdomain"
								disabled
								handleSubmit={handleChange}
								label="Default domain"
								toolTip="To use a custom domain, add a CNAME record pointing to your GoDesk domain. Then, contact support using the button at the top right of the screen so we can activate it on our end."
							/>
						</div>

						<div className="my-5">
							<ImageUpload
								label="Default favicon"
								fileUrl={data?.get("helpdesk.favicon.path") != "undefined" ? data?.get("helpdesk.favicon.path") ?? "" : ""}
								fileBase64={favicon.pictureData}
								updateFile={newFaviconCallback}
								height={40}
								width={"100%"}
								defaultImg={"/favicon.ico"}
							/>
						</div>

						<div className="my-5">
							<ImageUpload
								label="Default knowledge base logo (display height: 40px)"
								fileUrl={data?.get("helpdesk.logo") != "undefined" ? data?.get("helpdesk.logo") ?? "" : ""}
								fileBase64={logo.pictureData}
								updateFile={newLogoCallback}
								height={40}
								width={"100%"}
								defaultImg={"/logowithtext.png"}
							/>
						</div>

						<div className="my-5">
							<ImageUpload
								label="Default knowledge base banner (display height: 256px)"
								fileUrl={data?.get("helpdesk.banner.path") != "undefined" ? data?.get("helpdesk.banner.path") ?? "" : ""}
								fileBase64={banner.pictureData}
								updateFile={newKbBannerCallback}
								height={100}
								width={"100%"}
								defaultImg={"/knowledgebase.png"}
							/>
						</div>
					</FormSection>

					<div className="flex items-center ml-4 mb-4">
						<Button className="" onClick={cancelButtonClick} label="Cancel" />
						<input type="submit" className="ml-2 btn-blue" value="Update" />
					</div>
				</FormParent>
			</form>
			:
			null
	);
}
